import { useState } from 'react';
import { collection, orderBy, query, where } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { matchStatusLabel } from '@/lib/format';
import type { Match, Opportunity } from '@/types/domain';
import { durationLabel } from '@/lib/format';
import { strings } from '@/strings';

const candidateRespond = callable<
  { matchId: string; accepted: boolean },
  { matchId: string; status: string }
>('candidateRespond');

/**
 * What the candidate sees before anyone else does. Deliberately shows the
 * OPPORTUNITY and not the match score: the score answers a recruiter's
 * question about ranking, and showing someone the number attached to them
 * would change what they are deciding about.
 */
export function MyOpportunitiesPage() {
  const { claims } = useAuth();
  const candidateId = String(claims.candidateId || '');

  const { rows: matches, loading, error } = useDocs<Match>(
    candidateId
      ? query(collection(db, 'matches'), where('candidateId', '==', candidateId), orderBy('createdAt', 'desc'))
      : null,
    [candidateId]
  );
  const { rows: opportunities } = useDocs<Opportunity>(
    query(collection(db, 'opportunities'), orderBy('createdAt', 'desc')), []
  );
  const byId = new Map(opportunities.map((o) => [o.id, o]));

  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function answer(matchId: string, accepted: boolean) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      await candidateRespond({ matchId, accepted });
      setMessage(strings.matchAnswered);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.myOpportunitiesTitle}</h1>
      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && matches.length === 0 ? <p className="text-muted">{strings.myOpportunitiesEmpty}</p> : null}

      <ul className="flex flex-col gap-4">
        {matches.map((match) => {
          const opportunity = byId.get(match.opportunityId);
          return (
            <li key={match.id} className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-white p-4">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-lg font-semibold text-ink">{opportunity?.title || ''}</span>
                <span className="text-sm text-muted">{opportunity?.orgName || ''}</span>
              </div>
              <p className="text-ink">{opportunity?.summary || ''}</p>
              <p className="text-sm text-muted">
                {strings.fieldHours}: {opportunity?.hoursPerWeek ?? 0} — {strings.oppDurationKind}: {durationLabel(opportunity?.durationKind || '')}
              </p>

              {match.status === 'proposed' ? (
                <div className="flex gap-2">
                  <button type="button" disabled={busy} onClick={() => answer(match.id, true)}
                    className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                    {strings.matchAccept}
                  </button>
                  <button type="button" disabled={busy} onClick={() => answer(match.id, false)}
                    className="min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50">
                    {strings.matchDecline}
                  </button>
                </div>
              ) : (
                <span className="text-sm text-muted">{matchStatusLabel(match.status)}</span>
              )}
            </li>
          );
        })}
      </ul>

      {message ? <p className="text-sm text-success">{message}</p> : null}
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
    </div>
  );
}
